'use client';

import { useEffect, useRef, useState } from 'react';

const NON_TEXT_INPUTS = new Set(['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color', 'file', 'image', 'hidden']);
/** Long enough for the iOS keyboard to finish closing, so a blur never snaps the frame to the keyboard height. */
export const BLUR_SETTLE_MS = 400;

/**
 * A virtual keyboard can only be open on a touch device with a text-entry target focused. A focused
 * iframe counts: the payment step's Stripe card field is a cross-origin iframe. Trade-offs: an iPad with a
 * hardware keyboard still holds while a field is focused, and a Windows touch laptop (fine primary
 * pointer) never holds.
 */
export function keyboardMayBeOpen() {
  if (window.matchMedia?.('(pointer: coarse)').matches !== true) return false;
  const focused = document.activeElement;
  if (focused instanceof HTMLInputElement) return !NON_TEXT_INPUTS.has(focused.type);
  return focused instanceof HTMLTextAreaElement || focused instanceof HTMLSelectElement
    || focused instanceof HTMLIFrameElement || (focused instanceof HTMLElement && focused.isContentEditable);
}

/**
 * Keep the composed fuselage at its pre-keyboard height while a touch keyboard may be open; otherwise
 * follow the viewport, so a height-only resize never leaves a blank band under (or clips) the scene.
 */
export function useStableFrameHeight() {
  const [height, setHeight] = useState<number>();
  const width = useRef(0);
  useEffect(() => {
    const measure = () => {
      const viewport = window.visualViewport;
      const nextHeight = Math.round(viewport?.height ?? window.innerHeight);
      const nextWidth = Math.round(viewport?.width ?? window.innerWidth);
      const orientationChanged = width.current > 0 && Math.abs(nextWidth - width.current) > 48;
      const holdForKeyboard = keyboardMayBeOpen();
      // Keep the ref mutation outside the updater: React StrictMode may invoke
      // an updater twice, and the second invocation must see the same decision.
      width.current = nextWidth;
      setHeight((previous) => {
        return orientationChanged || previous === undefined || !holdForKeyboard ? nextHeight : previous;
      });
    };
    let blurTimer = 0;
    const onFocusOut = () => {
      window.clearTimeout(blurTimer);
      blurTimer = window.setTimeout(measure, BLUR_SETTLE_MS);
    };
    measure();
    window.visualViewport?.addEventListener('resize', measure);
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      window.clearTimeout(blurTimer);
      window.visualViewport?.removeEventListener('resize', measure);
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      document.removeEventListener('focusout', onFocusOut);
    };
  }, []);
  return height;
}
